import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface ShareContextValue {
  canonicalPath: string | null;
  setCanonicalPath: (path: string | null) => void;
}

export const ShareContext = createContext<ShareContextValue | null>(null);

export function ShareProvider({ children }: { children: ReactNode }) {
  const [canonicalPath, setCanonicalPath] = useState<string | null>(null);
  return (
    <ShareContext.Provider value={{ canonicalPath, setCanonicalPath }}>
      {children}
    </ShareContext.Provider>
  );
}

/**
 * Call from any page or card component to declare the canonical URL that
 * the AppShell share button should use. Clears automatically on unmount.
 *
 * Omit the argument (or pass undefined) to leave the share URL as-is,
 * which defaults to the current window.location in the share button.
 */
export function useShareableUrl(canonicalPath?: string) {
  const ctx = useContext(ShareContext);
  useEffect(() => {
    if (!ctx || canonicalPath === undefined) return;
    ctx.setCanonicalPath(canonicalPath);
    return () => ctx.setCanonicalPath(null);
  }, [ctx, canonicalPath]);
}

export function useCurrentSharePath(): string | null {
  return useContext(ShareContext)?.canonicalPath ?? null;
}
